<template>
  <div class="table-scroll-container">
    <div class="app-table">
      <div class="custom-table-header">
        <div
          v-for="(col, index) in columns"
          :key="'h-' + index"
          :style="colStyle(col)"
          :class="[
            'cell',
            'header-cell',
            alignClass(col.align),
            col.class,
            { 'is-sortable': col.sortable },
          ]"
          @click="onHeaderClick(col)"
        >
          <div class="header-content">
            <span class="header-label">{{ col.label }}</span>
            <VIcon
              v-if="col.sortable"
              :name="sortIcon(col)"
              class="sort-icon"
            />
          </div>
        </div>
      </div>
      <div
        v-for="(row, rowIndex) in rows"
        :key="'r-' + rowIndex"
        class="table-row title"
        :class="{ 'row-clickable': rowClick }"
        @click="rowClick && rowClick(row)"
      >
        <div
          v-for="(col, colIndex) in columns"
          :key="'c-' + rowIndex + '-' + colIndex"
          :style="colStyle(col)"
          :class="['cell', alignClass(col.align), col.class]"
          :title="!$slots[col.field] ? String(row[col.field] ?? '') : null"
        >
          <slot
            v-if="$slots[col.field]"
            :name="col.field"
            :row="row"
            :value="row[col.field]"
          ></slot>
          <div v-else class="cell-content">
            {{ row[col.field] }}
          </div>
        </div>
      </div>
      <div v-if="!rows || rows.length === 0" class="no-results">
        <VEmptyState icon="search-line" headline="No matching records found!" />
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "CustomTable",
  props: {
    columns: {
      type: Array,
      required: true,
    },
    rows: {
      type: Array,
      required: true,
    },
    sortField: {
      type: String,
      default: null,
    },
    sortDirection: {
      type: String,
      default: null,
    },
    rowClick: {
      type: Function,
      default: null,
    },
  },
  emits: ["sort"],
  data() {
    return {
      arrowUpOutline: 'arrow-up-line',
      arrowDownOutline: 'arrow-down-line',
      swapVerticalOutline: 'swap-vertical-line',
    };
  },
  methods: {
    colStyle(col) {
      const s = {};
      if (col.width) {
        s.flex = `0 0 ${col.width}`;
        s.maxWidth = col.width;
      } else {
        s.flex = '1';
      }
      if (col.minWidth) s.minWidth = col.minWidth;
      if (col.maxWidth && !col.width) s.maxWidth = col.maxWidth;
      return s;
    },
    alignClass(align) {
      if (align === "center") return "align-center";
      if (align === "end" || align === "right") return "align-end";
      return "align-start";
    },
    onHeaderClick(col) {
      if (!col.sortable) return;
      let nextDir = "asc";
      if (this.sortField === col.field) {
        nextDir = this.sortDirection === "asc" ? "desc" : "asc";
      }
      this.$emit("sort", { field: col.field, direction: nextDir });
    },
    sortIcon(col) {
      if (!col.sortable) return this.swapVerticalOutline;
      if (this.sortField !== col.field || !this.sortDirection) {
        return this.swapVerticalOutline;
      }
      return this.sortDirection === "asc"
        ? this.arrowUpOutline
        : this.arrowDownOutline;
    },
  },
};
</script>

<style scoped>
.app-table {
  background-color: var(--white);
  border-radius: 5px;
  width: 100%;
}

.custom-table-header {
  display: flex;
  font-size: 14px;
  font-weight: 400;
  color: var(--gray-04);
  border-bottom: 1px solid var(--background-primary);
}

.custom-table-header .cell:last-child {
  text-align: right;
  margin-left: auto;
}

.header-cell .header-content {
  display: flex;
  align-items: center;
  gap: 5px;
  width: 100%;
}

.header-cell.is-sortable {
  cursor: pointer;
}

.sort-icon {
  flex-shrink: 0;
  font-size: 16px;
  color: var(--gray-04);
}

.cell {
  padding: 20px;
  white-space: normal;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cell-content {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.align-start {
  text-align: left;
  justify-content: flex-start;
}

.align-center {
  text-align: center;
  justify-content: center;
}

.align-end {
  text-align: right;
  justify-content: flex-end;
}

.table-row {
  display: flex;
  align-items: center;
  border-bottom: 1px solid #f1f1f1;
  transition: background 0.2s ease;
  color: var(--black);
}

.table-row:hover {
  background-color: #fafafa;
}

.no-results {
  padding: 32px 16px;
  text-align: center;
  color: var(--gray-04);
}

.table-scroll-container {
  width: 100%;
  overflow-x: hidden;
}

.row-clickable {
  cursor: pointer;
}

@media (max-width: 768px) {
  .table-scroll-container {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .app-table {
    min-width: 900px;
  }

  .table-scroll-container::-webkit-scrollbar {
    height: 6px;
  }

  .table-scroll-container::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 4px;
  }
}
.header-label {
  font-weight: 700;
  color: var(--black)
}
</style>
